import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Send, Users, ShieldCheck, AlertTriangle, UserPlus, X, Lock,
} from 'lucide-react';
import { COLORS } from '../data/mockData';
import {
  fetchGroupChatRoom, fetchGroupChatMessages, postGroupChatMessage,
  joinGroupChatRoom, fetchGroupChatConsent, inviteToGroupChat,
  fetchGroupChatVisibleMembers,
} from '../services/api';
import { useAuth } from '../context/AuthContext';

const CAT_LABELS = {
  sleep: 'Sleep', headspace: 'Headspace', interaction: 'Interaction',
  exercise: 'Exercise', limit_exposure: 'Limit Exposure', diet: 'Diet',
  environment: 'Environment', discovery: 'Discovery',
  genetics_biomarkers: 'Genetics & Biomarkers',
  general_longevity: 'General Longevity',
};

export default function GroupChatRoomPage() {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);
  const [consent, setConsent] = useState(null);
  const [showInvite, setShowInvite] = useState(false);
  const listRef = useRef(null);

  const isMember = room?.membership?.status === 'active';
  const isOwner = room?.owner_id === user?.user_id;

  const loadRoom = async () => {
    try {
      const r = await fetchGroupChatRoom(roomId);
      setRoom(r);
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not load room');
    }
  };
  const loadMessages = async () => {
    if (!isMember) return;
    try {
      const m = await fetchGroupChatMessages(roomId);
      setMessages(m);
    } catch {}
  };

  useEffect(() => { loadRoom(); fetchGroupChatConsent().then(setConsent); }, [roomId]);
  useEffect(() => { loadMessages(); }, [room?.membership?.status]);

  // Poll for new messages every 4s when member
  useEffect(() => {
    if (!isMember) return undefined;
    const id = setInterval(loadMessages, 4000);
    return () => clearInterval(id);
  }, [isMember, roomId]);

  // Auto-scroll
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true); setErr(null);
    const draft = text;
    setText('');
    try {
      await postGroupChatMessage(roomId, draft);
      loadMessages();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Message blocked by moderator. Please rephrase.');
      setText(draft);
    } finally {
      setSending(false);
    }
  };

  if (err && !room) {
    return (
      <div style={{ padding: 24, color: COLORS.error }}>{err}</div>
    );
  }
  if (!room) return <div style={{ padding: 24, color: COLORS.textSecondary }}>Loading…</div>;

  return (
    <div data-testid="group-chat-room" style={{ maxWidth: 880, margin: '0 auto', padding: '16px 24px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <button onClick={() => navigate('/dashboard/chat/groups')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
          <ArrowLeft size={22} color={COLORS.textPrimary} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.primary, letterSpacing: 1, textTransform: 'uppercase' }}>{CAT_LABELS[room.topic] || room.topic}</div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>{room.name}</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: COLORS.textSecondary }}>
          {room.visibility === 'invite_only' && <Lock size={12} />}
          <Users size={12} /> {room.member_count}
        </div>
        {isOwner && (
          <button data-testid="invite-btn" onClick={() => setShowInvite(true)} style={{ padding: '8px 12px', borderRadius: 9999, border: `1px solid ${COLORS.primary}`, backgroundColor: COLORS.surface, color: COLORS.primary, cursor: 'pointer', fontSize: 12, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
            <UserPlus size={13} /> Invite
          </button>
        )}
      </div>

      {!isMember && consent && (
        <ConsentGate
          consent={consent}
          allowedInterests={room.allowed_interests}
          onJoined={async () => { await loadRoom(); }}
          roomId={roomId}
        />
      )}

      {isMember && (
        <>
          <div
            data-testid="message-list"
            ref={listRef}
            style={{
              backgroundColor: COLORS.surface,
              borderRadius: 14,
              padding: 16,
              minHeight: 320,
              maxHeight: 520,
              overflowY: 'auto',
              marginBottom: 12,
            }}
          >
            {messages.length === 0 && (
              <div style={{ textAlign: 'center', color: COLORS.textSecondary, fontSize: 13, padding: '40px 0' }}>
                No messages yet — say hello and keep it SHIELDED-relevant.
              </div>
            )}
            {messages.map((m) => {
              const mine = m.user_id === user?.user_id;
              const redacted = m.status === 'redacted';
              return (
                <div key={m.message_id} data-testid={`msg-${m.message_id}`} style={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
                  <div style={{ maxWidth: '78%' }}>
                    {!mine && <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 2 }}>{m.name}</div>}
                    <div style={{
                      padding: '8px 12px',
                      borderRadius: 14,
                      backgroundColor: mine ? COLORS.primary : '#EDF1E5',
                      color: mine ? '#FFF' : COLORS.textPrimary,
                      fontSize: 14,
                      lineHeight: 1.4,
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      border: redacted ? '1px dashed #B58A00' : 'none',
                    }}>
                      {m.content}
                      {redacted && (
                        <div style={{ marginTop: 4, fontSize: 11, color: mine ? 'rgba(255,255,255,0.85)' : '#B58A00', display: 'flex', alignItems: 'center', gap: 4 }}>
                          <AlertTriangle size={11} /> Redacted by AI moderator
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: COLORS.textSecondary, marginTop: 2, textAlign: mine ? 'right' : 'left' }}>
                      {(m.created_at || '').replace('T', ' ').slice(0, 16)}
                      {m.sentiment?.label && <span style={{ marginLeft: 6 }}>· {m.sentiment.label}</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {err && (
            <div style={{ padding: 10, backgroundColor: '#FFF1E0', border: '1px solid #F0C795', borderRadius: 10, color: '#A65500', fontSize: 13, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={14} /> {err}
            </div>
          )}

          <div style={{ display: 'flex', gap: 8 }}>
            <textarea
              data-testid="message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              placeholder="Share an experience, ask a peer question…"
              maxLength={4000}
              style={{ flex: 1, padding: 12, borderRadius: 12, border: `1px solid ${COLORS.border}`, fontSize: 14, resize: 'vertical', minHeight: 56, maxHeight: 160 }}
            />
            <button
              data-testid="send-btn"
              onClick={send}
              disabled={sending || !text.trim()}
              style={{ padding: '0 18px', borderRadius: 12, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', cursor: 'pointer', fontSize: 14, fontWeight: 700, opacity: sending || !text.trim() ? 0.6 : 1, display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <Send size={15} /> {sending ? 'Sending…' : 'Send'}
            </button>
          </div>
        </>
      )}

      {showInvite && <InviteModal roomId={roomId} onClose={() => setShowInvite(false)} />}
    </div>
  );
}


function ConsentGate({ consent, allowedInterests, roomId, onJoined }) {
  const [blocks, setBlocks] = useState({});
  const [interests, setInterests] = useState([]);
  const [joining, setJoining] = useState(false);
  const [err, setErr] = useState(null);

  const allOk = Object.keys(consent.blocks).every((k) => blocks[k]);
  const ready = allOk && interests.length > 0;

  const toggleInterest = (k) => {
    setInterests((prev) => prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]);
  };

  const join = async () => {
    if (!ready) return;
    setJoining(true); setErr(null);
    try {
      await joinGroupChatRoom(roomId, {
        consent_version: consent.version,
        consent_blocks: blocks,
        interests,
      });
      onJoined?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not join room');
    } finally {
      setJoining(false);
    }
  };

  return (
    <div data-testid="consent-gate" style={{ backgroundColor: COLORS.surface, borderRadius: 18, padding: 20, marginBottom: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <ShieldCheck size={20} color={COLORS.primary} />
        <h2 style={{ fontSize: 17, fontWeight: 800, color: COLORS.textPrimary, margin: 0, fontFamily: 'Outfit, sans-serif' }}>Before you join</h2>
      </div>
      <p style={{ fontSize: 13, color: COLORS.textSecondary, marginBottom: 14 }}>
        Read each block carefully. Acceptance is logged with version <code style={{ backgroundColor: '#EDF1E5', padding: '1px 5px', borderRadius: 4 }}>{consent.version}</code>.
      </p>

      {Object.entries(consent.blocks).map(([key, body]) => (
        <label key={key} data-testid={`consent-${key}`} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: 12, borderRadius: 10, backgroundColor: blocks[key] ? `${COLORS.primary}10` : '#F7F7F3', marginBottom: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!blocks[key]} onChange={(e) => setBlocks({ ...blocks, [key]: e.target.checked })} style={{ marginTop: 3 }} />
          <div>
            <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 3 }}>{key.replace(/_/g, ' ')}</div>
            <div style={{ fontSize: 13, color: COLORS.textPrimary, lineHeight: 1.5 }}>{body}</div>
          </div>
        </label>
      ))}

      <div style={{ marginTop: 12 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Your interest categories</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {(allowedInterests && allowedInterests.length ? allowedInterests : Object.keys(CAT_LABELS)).map((k) => (
            <button
              key={k}
              type="button"
              data-testid={`interest-${k}`}
              onClick={() => toggleInterest(k)}
              style={{
                padding: '6px 12px', borderRadius: 9999, border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600,
                backgroundColor: interests.includes(k) ? COLORS.primary : `${COLORS.primary}15`,
                color: interests.includes(k) ? '#FFF' : COLORS.primary,
              }}
            >{CAT_LABELS[k] || k}</button>
          ))}
        </div>
      </div>

      {err && <div style={{ color: COLORS.error, fontSize: 13, marginTop: 10 }}>{err}</div>}

      <button
        data-testid="accept-and-join"
        onClick={join}
        disabled={!ready || joining}
        style={{
          marginTop: 14, padding: '12px 22px', borderRadius: 9999, border: 'none', cursor: 'pointer',
          backgroundColor: ready ? COLORS.primary : COLORS.border,
          color: '#FFF', fontSize: 14, fontWeight: 700, opacity: joining ? 0.7 : 1,
        }}
      >
        {joining ? 'Joining…' : 'Accept & Join Room'}
      </button>
    </div>
  );
}


function InviteModal({ roomId, onClose }) {
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);
  const [search, setSearch] = useState('');
  const [directory, setDirectory] = useState([]);
  const [loadingDir, setLoadingDir] = useState(true);

  React.useEffect(() => {
    setLoadingDir(true);
    fetchGroupChatVisibleMembers(search)
      .then((r) => setDirectory(r.members || []))
      .finally(() => setLoadingDir(false));
  }, [search]);

  const submit = async (rawEmail) => {
    const target = (rawEmail || email).trim();
    if (!target.includes('@')) { setErr('Enter a valid email'); return; }
    setBusy(true); setErr(null);
    try {
      await inviteToGroupChat(roomId, target);
      setDone(`Invite sent to ${target}`);
      setEmail('');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Invite failed');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: COLORS.background, borderRadius: 14, width: '100%', maxWidth: 480, padding: 20, maxHeight: '85vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: COLORS.textPrimary, fontFamily: 'Outfit, sans-serif' }}>Invite a member</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={18} /></button>
        </div>

        {/* Member directory (opted-in members) */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase', marginBottom: 6 }}>Member Directory</div>
          <input
            data-testid="invite-directory-search"
            type="text"
            placeholder="Search members who opted in to invitations"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: '100%', padding: 9, borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 13, marginBottom: 8 }}
          />
          <div style={{ maxHeight: 200, overflowY: 'auto', borderRadius: 8, border: `1px solid ${COLORS.border}40` }}>
            {loadingDir && <div style={{ padding: 10, fontSize: 12, color: COLORS.textSecondary }}>Searching…</div>}
            {!loadingDir && directory.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: COLORS.textSecondary, textAlign: 'center' }}>
                No opted-in members{search ? ' match this search' : ' yet'}. Members must enable “Allow Group-Blog Invitations” in their Profile to appear here.
              </div>
            )}
            {directory.map((m) => (
              <div key={m.user_id} data-testid={`directory-row-${m.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: `1px solid ${COLORS.border}30` }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.textPrimary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || m.email}</div>
                  <div style={{ fontSize: 11, color: COLORS.textSecondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.email}</div>
                </div>
                <button
                  data-testid={`invite-member-${m.user_id}`}
                  onClick={() => submit(m.email)}
                  disabled={busy}
                  style={{ padding: '6px 12px', borderRadius: 9999, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', fontSize: 12, fontWeight: 700, cursor: busy ? 'wait' : 'pointer' }}
                >
                  Invite
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Raw email fallback */}
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: COLORS.primary, textTransform: 'uppercase', marginBottom: 6 }}>Or invite by email</div>
        <input data-testid="invite-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@example.com" style={{ width: '100%', padding: 10, borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, marginBottom: 10 }} />

        {done && <div style={{ color: COLORS.success, fontSize: 13, marginBottom: 8 }}>{done}</div>}
        {err && <div style={{ color: COLORS.error, fontSize: 13, marginBottom: 8 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`, backgroundColor: COLORS.surface, cursor: 'pointer', fontSize: 13, fontWeight: 600, color: COLORS.textPrimary }}>Done</button>
          <button data-testid="send-invite-btn" onClick={() => submit()} disabled={busy || !email.includes('@')} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', backgroundColor: COLORS.primary, color: '#FFF', cursor: 'pointer', fontSize: 13, fontWeight: 700, opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Sending…' : 'Send Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}
